import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Paper,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Folder as FolderIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';

// Component information
const COMPONENT_INFO = {
  name: 'MarkupMAIQ',
  path: 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ',
  folderStructure: [
    'app',
    'components',
    'ReusableUIComponents',
    'HighLevelComponents',
    'MarkupMAIQ',
  ],
};

export const ComponentsTest: React.FC = () => {
  const [rawContent, setRawContent] = useState('');

  // Normalize content: Convert literal \n strings to actual newlines
  // This ensures the content matches what will be passed to MarkupMAIQ in real scenarios
  const normalizeContent = (content: string): string => {
    if (!content) return '';
    // Replace literal \n with actual newlines
    return content.replace(/\\n/g, '\n');
  };

  // Get normalized content for rendering
  const normalizedContent = normalizeContent(rawContent);

  const handleCopyContent = async () => {
    try {
      // Copy the normalized content (what will actually be used)
      await navigator.clipboard.writeText(normalizedContent);
      console.log('Content copied to clipboard');
    } catch (err) {
      console.error('Failed to copy content:', err);
    }
  };

  const handleClearContent = () => {
    setRawContent('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Component Info Section */}
      <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CodeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Component Information
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Component Name:
            </Typography>
            <Chip
              label={COMPONENT_INFO.name}
              size="small"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <FolderIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Folder Structure:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {COMPONENT_INFO.folderStructure.map((folder, index) => (
                  <React.Fragment key={index}>
                    <Chip
                      label={folder}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.7rem',
                        height: 22,
                        bgcolor: 'action.hover',
                      }}
                    />
                    {index < COMPONENT_INFO.folderStructure.length - 1 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                        /
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 1,
                  display: 'block',
                  fontFamily: 'monospace',
                  bgcolor: 'background.paper',
                  p: 0.5,
                  borderRadius: 0.5,
                }}
              >
                {COMPONENT_INFO.path}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Input Section */}
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Raw Content Input
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyContent}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearContent}
              >
                Clear
              </Button>
            </Box>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={8}
            value={rawContent}
            onChange={(e) => setRawContent(e.target.value)}
            placeholder="Enter markdown content here..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter your markdown content above to see how it renders below. 
            <br />
            <strong>Note:</strong> Literal <code style={{ backgroundColor: 'rgba(0,0,0,0.06)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>\n</code> strings will be converted to actual newlines automatically.
          </Typography>
        </CardContent>
      </Card>

      <Divider />

      {/* Output Section */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Rendered Output
          </Typography>
          
          <Box
            sx={{
              flex: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              overflow: 'auto',
              minHeight: 200,
              maxHeight: 400,
            }}
          >
            {normalizedContent ? (
              <MarkupMAIQ content={normalizedContent} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Enter content above to see the rendered output here
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

